function validateDocumentRUT(rut) {
  rut = rut.replace(/\./g, '').replace(/-/g, '').toUpperCase().trim();
  if (!/^\d{7,8}[0-9K]$/.test(rut)) return false;

  const body = rut.slice(0, -1);
  const dv = rut.slice(-1);
  let sum = 0;
  let multiplier = 2;

  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  let calculatedDV = 11 - remainder;

  if (calculatedDV === 11) calculatedDV = '0';
  else if (calculatedDV === 10) calculatedDV = 'K';
  else calculatedDV = String(calculatedDV);

  return calculatedDV === dv;
}

function formatDocumentRUT(rut) {
  rut = rut.replace(/[^0-9Kk]/g, '').toUpperCase();
  if (!rut.length) return '';

  const body = rut.slice(0, -1);
  const dv = rut.slice(-1);
  let formattedBody = '';

  for (let i = body.length - 1; i >= 0; i--) {
    formattedBody = body[i] + formattedBody;
    if ((body.length - i) % 3 === 0 && i > 0) formattedBody = '.' + formattedBody;
  }

  return `${formattedBody}-${dv}`;
}

class DocumentType extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.regions = window.CHILE_REGIONS || {};
  }

  connectedCallback() {
    requestAnimationFrame(() => this.init());
  }

  init() {
    const radioButtons = this.querySelectorAll('input[type="radio"][name="document_type"]');
    if (!radioButtons.length) return;
    if (this.dataset.bound === 'true') return;

    this.dataset.bound = 'true';

    const savedType = this.dataset.savedType || 'boleta';
    const savedRadio = this.querySelector(`input[value="${savedType}"]`);
    if (savedRadio) {
      savedRadio.checked = true;
      this.toggleForm(savedType === 'factura');
    }

    radioButtons.forEach((radio) => {
      if (radio.dataset.bound === 'true') return;
      radio.dataset.bound = 'true';
      radio.addEventListener('change', (event) => {
        const selectedType = event.target.value;
        this.toggleForm(selectedType === 'factura');
        this.updateCartAttribute('document_type', selectedType);

        if (selectedType === 'boleta') {
          this.clearFacturaFormInputs();
          this.clearFacturaData();
          this.enableCheckoutButton();
        } else {
          requestAnimationFrame(() => this.validateAllFields());
        }
      });
    });

    this.initRegionDropdown();
    this.initFormFields();
    this.initialized = true;
  }

  toggleForm(show) {
    const facturaForm = this.querySelector('.factura-form');
    if (!facturaForm) return;

    facturaForm.classList.toggle('mini-cart-pilgrim__factura-form--visible', show);
    facturaForm.hidden = !show;
  }

  initRegionDropdown() {
    const regionSelect = this.querySelector('#mini-cart-factura-region');
    if (!regionSelect || regionSelect.dataset.populated === 'true') return;

    Object.keys(this.regions).forEach((region) => {
      const option = document.createElement('option');
      option.value = region;
      option.textContent = region;
      regionSelect.appendChild(option);
    });

    regionSelect.dataset.populated = 'true';

    const savedRegion = this.dataset.savedRegion;
    if (savedRegion) {
      regionSelect.value = savedRegion;
      this.updateCommunes(savedRegion, this.dataset.savedComuna);
    }

    regionSelect.addEventListener('change', (event) => {
      this.updateCommunes(event.target.value);
      this.updateCartAttribute('Región', event.target.value);
      this.updateCartAttribute('Comuna', '');
      this.validateAllFields();
    });
  }

  updateCommunes(region, savedCommune = null) {
    const communeSelect = this.querySelector('#mini-cart-factura-comuna');
    if (!communeSelect) return;

    communeSelect.innerHTML = '<option value="">Selecciona una comuna</option>';
    (this.regions[region] || []).forEach((commune) => {
      const option = document.createElement('option');
      option.value = commune;
      option.textContent = commune;
      if (savedCommune === commune) option.selected = true;
      communeSelect.appendChild(option);
    });
  }

  initFormFields() {
    const rutInput = this.querySelector('#mini-cart-factura-rut');
    const giroInput = this.querySelector('#mini-cart-factura-giro');
    const razonSocialInput = this.querySelector('#mini-cart-factura-razon-social');
    const direccionInput = this.querySelector('#mini-cart-factura-direccion');
    const emailInput = this.querySelector('#mini-cart-factura-email');
    const communeSelect = this.querySelector('#mini-cart-factura-comuna');

    if (rutInput) {
      rutInput.addEventListener('input', (event) => {
        const value = event.target.value;
        const cursorPosition = event.target.selectionStart;
        const formatted = formatDocumentRUT(value);
        if (formatted !== value) {
          event.target.value = formatted;
          const newPosition = Math.min(cursorPosition + (formatted.length - value.length), formatted.length);
          event.target.setSelectionRange(newPosition, newPosition);
        }
      });

      const validateRUTInput = debounce((event) => {
        const value = event.target.value.trim();
        const fieldContainer = event.target.closest('.mini-cart-pilgrim__factura-field');
        const errorMessage = fieldContainer?.querySelector('.rut-error-message');

        if (!value) {
          errorMessage?.remove();
          event.target.classList.remove('invalid');
          this.updateCartAttribute('RUTEmpresa', '');
          this.validateAllFields();
          return;
        }

        const isValid = validateDocumentRUT(value);
        if (!isValid) {
          if (!errorMessage) {
            const message = document.createElement('span');
            message.className = 'rut-error-message mini-cart-pilgrim__field-error';
            message.textContent = 'RUT inválido. Por favor ingrese un RUT válido (ej: 12.345.678-9)';
            fieldContainer?.appendChild(message);
          }
          event.target.classList.add('invalid');
        } else {
          errorMessage?.remove();
          event.target.classList.remove('invalid');
          this.updateCartAttribute('RUTEmpresa', value);
        }
        this.validateAllFields();
      }, 400);

      rutInput.addEventListener('input', validateRUTInput);
      rutInput.addEventListener('blur', validateRUTInput);
    }

    if (emailInput) {
      emailInput.addEventListener('blur', () => {
        const value = emailInput.value.trim();
        const fieldContainer = emailInput.closest('.mini-cart-pilgrim__factura-field');
        const errorMessage = fieldContainer?.querySelector('.email-error-message');
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

        if (!value) {
          errorMessage?.remove();
          emailInput.classList.remove('invalid');
        } else if (!isValid) {
          if (!errorMessage) {
            const message = document.createElement('span');
            message.className = 'email-error-message mini-cart-pilgrim__field-error';
            message.textContent = 'Por favor ingrese un correo electrónico válido';
            fieldContainer?.appendChild(message);
          }
          emailInput.classList.add('invalid');
        } else {
          errorMessage?.remove();
          emailInput.classList.remove('invalid');
        }
        this.validateAllFields();
      });
    }

    const fieldMap = [
      [giroInput, 'Giro'],
      [razonSocialInput, 'RazonSocial'],
      [direccionInput, 'DireccionEmpresa'],
      [emailInput, 'Correo Electrónico']
    ];

    fieldMap.forEach(([input, attributeName]) => {
      if (!input) return;
      input.addEventListener('input', debounce((event) => {
        this.updateCartAttribute(attributeName, event.target.value);
        this.validateAllFields();
      }, 300));
    });

    communeSelect?.addEventListener('change', (event) => {
      this.updateCartAttribute('Comuna', event.target.value);
      this.validateAllFields();
    });

    requestAnimationFrame(() => this.validateAllFields());
  }

  validateAllFields() {
    const isFactura = this.querySelector('input[value="factura"]:checked') !== null;
    if (!isFactura) {
      this.enableCheckoutButton();
      return true;
    }

    const rutInput = this.querySelector('#mini-cart-factura-rut');
    const giroInput = this.querySelector('#mini-cart-factura-giro');
    const razonSocialInput = this.querySelector('#mini-cart-factura-razon-social');
    const direccionInput = this.querySelector('#mini-cart-factura-direccion');
    const emailInput = this.querySelector('#mini-cart-factura-email');
    const regionSelect = this.querySelector('#mini-cart-factura-region');
    const communeSelect = this.querySelector('#mini-cart-factura-comuna');

    const rutValue = rutInput?.value.trim() || '';
    const emailValue = emailInput?.value.trim() || '';
    const rutValid = rutValue !== '' && validateDocumentRUT(rutValue);
    const emailValid = emailValue !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

    const allFieldsFilled =
      rutValid &&
      (giroInput?.value.trim() || '') !== '' &&
      (razonSocialInput?.value.trim() || '') !== '' &&
      (direccionInput?.value.trim() || '') !== '' &&
      (regionSelect?.value || '') !== '' &&
      (communeSelect?.value || '') !== '' &&
      emailValid;

    if (allFieldsFilled) this.enableCheckoutButton();
    else this.disableCheckoutButton();

    return allFieldsFilled;
  }

  getCheckoutButton() {
    return document.querySelector('.mini-cart-pilgrim__checkout, button[name="checkout"]');
  }

  enableCheckoutButton() {
    const checkoutButton = this.getCheckoutButton();
    if (!checkoutButton) return;
    checkoutButton.removeAttribute('disabled');
    checkoutButton.classList.remove('disabled');
  }

  disableCheckoutButton() {
    const checkoutButton = this.getCheckoutButton();
    if (!checkoutButton) return;
    checkoutButton.setAttribute('disabled', 'disabled');
    checkoutButton.classList.add('disabled');
  }

  updateCartAttribute(key, value) {
    fetch(`${theme.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes: { [key]: value } })
    }).catch((error) => console.error(error));
  }

  clearFacturaFormInputs() {
    this.querySelector('#mini-cart-factura-rut')?.closest('.mini-cart-pilgrim__factura-field')?.querySelector('.rut-error-message')?.remove();
    this.querySelector('#mini-cart-factura-email')?.closest('.mini-cart-pilgrim__factura-field')?.querySelector('.email-error-message')?.remove();

    ['#mini-cart-factura-rut', '#mini-cart-factura-giro', '#mini-cart-factura-razon-social', '#mini-cart-factura-direccion', '#mini-cart-factura-email'].forEach((selector) => {
      const input = this.querySelector(selector);
      if (!input) return;
      input.value = '';
      input.classList.remove('invalid');
    });

    const regionSelect = this.querySelector('#mini-cart-factura-region');
    const communeSelect = this.querySelector('#mini-cart-factura-comuna');
    if (regionSelect) regionSelect.value = '';
    if (communeSelect) communeSelect.innerHTML = '<option value="">Selecciona una comuna</option>';
  }

  clearFacturaData() {
    this.updateCartAttributes({
      RUTEmpresa: '',
      Giro: '',
      RazonSocial: '',
      DireccionEmpresa: '',
      'Región': '',
      Comuna: '',
      'Correo Electrónico': ''
    });
  }

  updateCartAttributes(attributes) {
    fetch(`${theme.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes })
    }).catch((error) => console.error(error));
  }
}

if (!customElements.get('document-type')) {
  customElements.define('document-type', DocumentType);
}

function initializeDocumentTypes() {
  document.querySelectorAll('document-type').forEach((element) => {
    element.initialized = false;
    element.init();
  });
}

window.initializeDocumentTypes = initializeDocumentTypes;

document.addEventListener('cartdrawer:opened', () => {
  requestAnimationFrame(initializeDocumentTypes);
});

document.addEventListener('cart:updated', () => {
  requestAnimationFrame(initializeDocumentTypes);
});

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    requestAnimationFrame(initializeDocumentTypes);
  });
}
